import { useState } from "react";
import {
  Box,
  FormControl,
  FormControlLabel,
  RadioGroup,
  Radio,
  Typography,
  Grid,
  IconButton,
  Chip,
  Divider,
} from "@mui/material";
import {
  ContentCopy,
  CheckCircle,
  AccountBalance,
  CreditCard,
  HelpOutline,
  Receipt,
} from "@mui/icons-material";
import SelectHelpSeller from "../../../containers/SelectOptions/HelpForSeller";
import SelectInvoice from "../../../containers/SelectOptions/SelectInvoice";
import OpenPayForm from "../CheckoutOrder/OpenPay/OpenPayForm";
import EmailVerification from "../EmailVerification";

// ── Paleta MerryColor ───────────────────────────────────────
const MC = {
  primary: "#801F56",
  primaryLight: "#A93B7A",
  accent: "#E46EA7",
  accentSoft: "#F3CDD4",
  blushLight: "#FFF0F5",
  white: "#FFFFFF",
  dark: "#2D1B30",
  grey: "#6B6B6B",
  success: "#4CAF50",
};

export const CheckoutStep2 = ({
  onNext,
  onPrev,
  setHelpSeller,
  setInvoice,
  paymentType,
  setPaymentType,
  openPayRef,
  setSellerName,
}) => {
  const [copied, setCopied] = useState("");

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(text);
    setTimeout(() => setCopied(""), 2000);
  };

  const detectarCambiosHelpForSeller = (value) => {
    setHelpSeller(value.value);
  };

  const detectarCambiosInvoice = (value) => {
    setInvoice(value.value);
  };

  const CopyField = ({ label, value }) => (
    <Box sx={{ mb: 1.5 }}>
      <Typography variant="caption" color={MC.grey} fontWeight={600}>
        {label}
      </Typography>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.5,
          background: MC.blushLight,
          borderRadius: 1.5,
          px: 1.5,
          py: 0.8,
          mt: 0.3,
        }}
      >
        <Typography
          variant="body2"
          sx={{ fontWeight: 700, color: MC.dark, flex: 1 }}
        >
          {value}
        </Typography>
        <IconButton
          size="small"
          onClick={() => handleCopy(value)}
          sx={{
            color: copied === value ? MC.success : MC.accent,
            transition: "color 0.2s",
          }}
        >
          {copied === value ? (
            <CheckCircle fontSize="small" />
          ) : (
            <ContentCopy fontSize="small" />
          )}
        </IconButton>
      </Box>
    </Box>
  );

  return (
    <>
      <Box
        sx={{
          p: 3,
          borderRadius: 3,
          border: `2px solid ${MC.accentSoft}`,
          background: `linear-gradient(145deg, ${MC.blushLight} 0%, ${MC.white} 100%)`,
        }}
      >
        {/* ─── Header ──────────────────────────────────────── */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 3 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 2,
              background: `linear-gradient(135deg, ${MC.primary} 0%, ${MC.accent} 100%)`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <CreditCard sx={{ color: MC.white, fontSize: 22 }} />
          </Box>
          <Box>
            <Typography variant="h6" fontWeight={700} color={MC.dark}>
              Método de pago
            </Typography>
            <Typography variant="caption" color={MC.grey}>
              Selecciona cómo deseas pagar tu pedido
            </Typography>
          </Box>
        </Box>

        {/* ─── Opción: Transferencia Bancaria ──────────────── */}
        <Box
          onClick={() => setPaymentType("transferencia")}
          sx={{
            border: 2,
            borderColor: paymentType === "transferencia" ? MC.accent : MC.accentSoft,
            borderRadius: 3,
            p: 2.5,
            mb: 2,
            cursor: "pointer",
            transition: "all 0.25s ease",
            background: paymentType === "transferencia" ? MC.blushLight : MC.white,
            "&:hover": {
              borderColor: MC.accent,
              boxShadow: "0 4px 16px rgba(228, 110, 167, 0.15)",
            },
          }}
        >
          <FormControl sx={{ width: "100%" }}>
            <RadioGroup
              value={paymentType}
              onChange={(e) => setPaymentType(e.target.value)}
            >
              <FormControlLabel
                value="transferencia"
                control={
                  <Radio
                    sx={{
                      color: MC.accentSoft,
                      "&.Mui-checked": { color: MC.primary },
                    }}
                  />
                }
                label={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <AccountBalance sx={{ color: MC.primary, fontSize: 20 }} />
                    <Typography variant="body1" fontWeight={700} color={MC.dark}>
                      Transferencia Bancaria
                    </Typography>
                  </Box>
                }
              />
            </RadioGroup>
          </FormControl>

          {paymentType === "transferencia" && (
            <Box sx={{ mt: 2 }}>
              <Divider sx={{ mb: 2, borderColor: MC.accentSoft }} />
              <Typography
                variant="subtitle2"
                fontWeight={700}
                color={MC.primary}
                sx={{ mb: 1.5 }}
              >
                Datos para transferencia:
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <CopyField label="Titular" value="Importadora MerryColor SAPI de CV" />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <CopyField label="CLABE" value="012914002181549867" />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <CopyField label="Banco" value="BBVA" />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <CopyField label="No. de cuenta" value="0218154986" />
                </Grid>
              </Grid>

              <Chip
                icon={<CheckCircle sx={{ fontSize: 16 }} />}
                label={copied ? "¡Copiado!" : "Toca el ícono para copiar"}
                size="small"
                sx={{
                  mt: 1.5,
                  background: copied ? "#E8F5E9" : MC.blushLight,
                  color: copied ? MC.success : MC.grey,
                  fontWeight: 500,
                  fontSize: "0.75rem",
                }}
              />
            </Box>
          )}
        </Box>

        {/* ─── Opción: Pago con Tarjeta ────────────────────── */}
        <Box
          onClick={() => setPaymentType("openpay")}
          sx={{
            border: 2,
            borderColor: paymentType === "openpay" ? MC.accent : MC.accentSoft,
            borderRadius: 3,
            p: 2.5,
            mb: 2,
            cursor: "pointer",
            transition: "all 0.25s ease",
            background: paymentType === "openpay" ? MC.blushLight : MC.white,
            "&:hover": {
              borderColor: MC.accent,
              boxShadow: "0 4px 16px rgba(228, 110, 167, 0.15)",
            },
          }}
        >
          <FormControl sx={{ width: "100%" }}>
            <RadioGroup
              value={paymentType}
              onChange={(e) => setPaymentType(e.target.value)}
            >
              <FormControlLabel
                value="openpay"
                control={
                  <Radio
                    sx={{
                      color: MC.accentSoft,
                      "&.Mui-checked": { color: MC.primary },
                    }}
                  />
                }
                label={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <CreditCard sx={{ color: MC.primary, fontSize: 20 }} />
                    <Typography variant="body1" fontWeight={700} color={MC.dark}>
                      Pago con Tarjeta
                    </Typography>
                    <Chip
                      label="3D Secure"
                      size="small"
                      sx={{
                        background: `linear-gradient(135deg, ${MC.primary}, ${MC.accent})`,
                        color: MC.white,
                        fontWeight: 700,
                        fontSize: "0.65rem",
                        height: 22,
                      }}
                    />
                  </Box>
                }
              />
            </RadioGroup>
          </FormControl>

          {paymentType === "openpay" && (
            <Box sx={{ mt: 2 }}>
              <EmailVerification />
              <Box sx={{ mt: 2 }}>
                <OpenPayForm ref={openPayRef} />
              </Box>
            </Box>
          )}
        </Box>

        <Divider sx={{ my: 3, borderColor: MC.accentSoft }} />

        {/* ─── Preguntas adicionales ───────────────────────── */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
          <HelpOutline sx={{ color: MC.accent, fontSize: 20 }} />
          <Typography variant="subtitle1" fontWeight={700} color={MC.dark}>
            Información adicional
          </Typography>
        </Box>

        <Grid container spacing={2.5}>
          <Grid item xs={12} sm={6}>
            <Typography
              variant="body2"
              fontWeight={600}
              color={MC.dark}
              sx={{ mb: 0.8 }}
            >
              ¿Recibiste ayuda de una asesora de ventas?
            </Typography>
            <SelectHelpSeller
              detectarCambiosHelpForSeller={detectarCambiosHelpForSeller}
              onSellerChange={(name) => setSellerName && setSellerName(name)}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.8 }}>
              <Receipt sx={{ color: MC.accent, fontSize: 18 }} />
              <Typography variant="body2" fontWeight={600} color={MC.dark}>
                ¿Requieres factura?
              </Typography>
            </Box>
            <SelectInvoice detectarCambiosInvoice={detectarCambiosInvoice} />
          </Grid>
        </Grid>
      </Box>
    </>
  );
};
